import { useRef, useState } from 'react';
import { uploadFile } from '../../../functions/Api';
import { Button } from '@asu/unity-react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import errorLogger from '../../../utils/ErrorLogger';

const UploadByFile = (props) => {
  const [file, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  const clearFileInput = () => {
    setUploadFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFiles = async () => {
    props.setUploading(true);
    let hasError = false;

    for (const selectedClass of props.classes) {
      try {
        let result = await uploadFile(file, selectedClass);
        props.setUploadResults((results) => results.concat(result));
      } catch (error) {
        hasError = true;
        const errorMessage = 'Error: ' + (error.message || 'Unknown upload error');
        
        errorLogger.logError(error, 'upload', {
          classNumber: selectedClass.classNumber,
          filename: file?.name
        });

        let errors = props.errors;
        if (!errors.includes(errorMessage)) {
          errors.push(errorMessage);
        }
        props.setErrors(errors);
        props.setUploading(false);
        return error;
      }
    }

    if (!hasError) {
      props.updateSyllabusStatus();
    }
    props.setUploading(false);
  };

  const onChangeHandler = (event) => {
    const selectedFile = event.target.files[0];
    
    if (selectedFile) {
      // Enhanced PDF validation - check both MIME type and file extension
      const isPdfExtension = selectedFile.name.toLowerCase().endsWith('.pdf');
      
      if (!isPdfExtension) {
        errorLogger.logWarning('Invalid file extension selected', 'upload', {
          filename: selectedFile.name,
          fileExtension: selectedFile.name.split('.').pop()?.toLowerCase()
        });
        
        alert('Please select a PDF file only. The file must have a .pdf extension.');
        clearFileInput();
        return;
      }

      // Additional validation: check if MIME type is clearly not a PDF
      const isDefinitelyNotPdf = selectedFile.type && 
                                (selectedFile.type.startsWith('image/') ||
                                selectedFile.type.startsWith('text/') ||
                                selectedFile.type.startsWith('video/') ||
                                selectedFile.type.startsWith('audio/') ||
                                selectedFile.type === 'application/msword' ||
                                selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');

      if (isDefinitelyNotPdf) {
        errorLogger.logWarning('File type is definitely not PDF', 'upload', {
          filename: selectedFile.name,
          fileType: selectedFile.type
        });
        
        alert('Please select a PDF file only. The selected file appears to be a different format.');
        clearFileInput();
        return;
      }

      // Validate file size (50MB limit)
      const maxSize = 50 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        errorLogger.logWarning('File too large', 'upload', {
          filename: selectedFile.name,
          fileSize: selectedFile.size
        });
        
        alert('File is too large. Please select a file smaller than 50MB.');
        clearFileInput();
        return;
      }
    }
    
    setUploadFile(selectedFile);
  };

  const handleUpload = () => {
    if (file) {
      uploadFiles();
    } else {
      alert('No file chosen');
    }
  };

  return (
    <div className="action-buttons mb-2">
      <div className="d-flex gap-1 mb-2">
        <input
          type="file"
          name="file"
          onChange={onChangeHandler}
          ref={fileInputRef}
          accept=".pdf,application/pdf"
        />
        {/** Display delete icon if there is an upload file */}
        {file && (
          <button
            value="remove"
            className="border-0 rounded-circle bg-transparent text-maroon"
            onClick={clearFileInput}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
      <div>
        <Button label="Upload" color="maroon" onClick={handleUpload} />
      </div>
    </div>
  );
};

export default UploadByFile;
